import React, { useState, useRef, useEffect } from 'react';
import { format, isToday, isYesterday } from 'date-fns';
import { Message, MessageReaction } from '../../types';
import { MdDelete, MdAddReaction, MdReply } from 'react-icons/md';
import EmojiPicker from './EmojiPicker';
import { parseUtcDate } from '../../utils/dateFormat';
import { extractFirstYouTubeUrl } from '../../utils/youtube';
import { extractFirstAmazonMusicUrl } from '../../utils/amazonMusic';
import { YouTubePreview } from './YouTubePreview';
import { AmazonMusicPreview } from './AmazonMusicPreview';
import axiosInstance from '../../api/axiosInstance';

const urlRegex = /(https?:\/\/[^\s]+)/g;
const trailingUrlPunctuationRegex = /[.,!?;:)]*$/;

const renderContentWithLinks = (content: string) => {
  const parts = content.split(urlRegex);

  return parts.map((part, index) => {
    if (!part.startsWith('http://') && !part.startsWith('https://')) {
      return <React.Fragment key={index}>{part}</React.Fragment>;
    }

    const trailingPunctuation = part.match(trailingUrlPunctuationRegex)?.[0] ?? '';
    const url = trailingPunctuation ? part.slice(0, -trailingPunctuation.length) : part;

    if (!url) {
      return <React.Fragment key={index}>{part}</React.Fragment>;
    }

    return (
      <React.Fragment key={index}>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium text-[#67E8F9] underline decoration-[#67E8F9]/60 underline-offset-2 break-all hover:text-white"
          onClick={(event) => event.stopPropagation()}
        >
          {url}
        </a>
        {trailingPunctuation}
      </React.Fragment>
    );
  });
};

interface MessageItemProps {
  message: Message;
  currentUserId: string;
  onImageClick: (imageUrl: string) => void;
  onAddReaction: (messageId: number, emoji: string) => void;
  onRemoveReaction: (reactionId: number) => void;
  onDeleteMessage: (messageId: number) => void;
  onReplyToMessage?: (message: Message) => void;
  otherUserName?: string;
}

const MessageItem: React.FC<MessageItemProps> = ({ 
  message, 
  currentUserId, 
  onImageClick,
  onAddReaction,
  onRemoveReaction,
  onDeleteMessage,
  onReplyToMessage,
  otherUserName = 'User'
}) => {
  const [showActions, setShowActions] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [emojiPickerPos, setEmojiPickerPos] = useState({ x: 0, y: 0 });
  const longPressTimer = useRef<NodeJS.Timeout | null>(null);
  const touchStartPos = useRef({ x: 0, y: 0 });
  const messageRef = useRef<HTMLDivElement>(null);
  const actionsMenuRef = useRef<HTMLDivElement>(null);
  const [mediaObjectUrl, setMediaObjectUrl] = useState<string | null>(null);

  const formatMessageTime = (dateString: string) => {
    try {
      const date = parseUtcDate(dateString);
      if (!date) return dateString;
      
      if (isToday(date)) {
        return format(date, 'h:mm a');
      } else if (isYesterday(date)) {
        return format(date, 'MMM d, h:mm a');
      } else {
        return format(date, 'MMM d, h:mm a');
      }
    } catch (error) {
      console.error('Error formatting message time:', error);
      return dateString;
    }
  };

  const isSender = message.fromUserId === parseInt(currentUserId);
  const isOfflineMessage = message.sentWhileOffline && !isSender;
  // Determine reply name: if the original message is from current user, show "You", otherwise show other user's name
  const replyToName = message.replyToMessage?.fromUserId === parseInt(currentUserId) ? 'You' : otherUserName;
  const youtubeUrl = message.type === 'YouTube' ? message.content : extractFirstYouTubeUrl(message.content);
  const amazonMusicUrl = message.type === 'AmazonMusic' ? message.content : extractFirstAmazonMusicUrl(message.content);
  const imageUrl = mediaObjectUrl || message.imageDataUrl;
  const voiceUrl = mediaObjectUrl || message.voiceDataUrl;

  useEffect(() => {
    const mediaUrl = message.type === 'Image' ? message.imageDataUrl : message.type === 'Voice' ? message.voiceDataUrl : undefined;

    if (!mediaUrl || mediaUrl.startsWith('data:') || mediaUrl.startsWith('blob:')) {
      setMediaObjectUrl(null);
      return;
    }

    let objectUrl: string | null = null;
    let cancelled = false;

    axiosInstance.get(mediaUrl, { responseType: 'blob' })
      .then((response) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(response.data);
        setMediaObjectUrl(objectUrl);
      })
      .catch((error) => {
        console.error('Failed to load message media:', error);
        setMediaObjectUrl(null);
      });

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [message.imageDataUrl, message.type, message.voiceDataUrl]);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartPos.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    
    longPressTimer.current = setTimeout(() => {
      setShowActions(true);
      // Adjust menu position after showing to prevent overflow
      setTimeout(() => adjustMenuPosition(), 10);
    }, 500);
  };

  const adjustMenuPosition = () => {
    if (!actionsMenuRef.current || !messageRef.current) return;
    
    const menu = actionsMenuRef.current;
    const menuRect = menu.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    
    // Check if menu overflows right edge
    if (menuRect.right > viewportWidth - 16) {
      const overflow = menuRect.right - (viewportWidth - 16);
      menu.style.transform = `translateX(-${overflow}px)`;
    }
    
    // Check if menu overflows left edge
    if (menuRect.left < 16) {
      const overflow = 16 - menuRect.left;
      menu.style.transform = `translateX(${overflow}px)`;
    }
  };

  const handleTouchEnd = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    const moveThreshold = 10;
    const deltaX = Math.abs(e.touches[0].clientX - touchStartPos.current.x);
    const deltaY = Math.abs(e.touches[0].clientY - touchStartPos.current.y);
    
    if (deltaX > moveThreshold || deltaY > moveThreshold) {
      if (longPressTimer.current) {
        clearTimeout(longPressTimer.current);
      }
    }
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowActions(true);
    // Adjust menu position after showing to prevent overflow
    setTimeout(() => adjustMenuPosition(), 10);
  };

  const handleReactClick = (e: React.MouseEvent) => {
    const rect = (e.target as HTMLElement).getBoundingClientRect();
    setEmojiPickerPos({ 
      x: rect.left + rect.width / 2, 
      y: rect.top 
    });
    setShowEmojiPicker(true);
    setShowActions(false);
  };

  const handleEmojiSelect = (emoji: string) => {
    onAddReaction(message.id, emoji);
    // Keep emoji picker open for multiple reactions
    // User can close it manually by clicking outside or the close button
  };

  const handleDelete = () => {
    if (window.confirm('Delete this message?')) {
      onDeleteMessage(message.id);
      setShowActions(false);
    }
  };

  // Group reactions by user ID instead of emoji
  const groupReactionsByUser = (reactions?: MessageReaction[]) => {
    if (!reactions || reactions.length === 0) return [];
    
    const grouped = reactions.reduce((acc, reaction) => {
      const userId = reaction.userId;
      if (!acc[userId]) {
        acc[userId] = [];
      }
      acc[userId].push(reaction);
      return acc;
    }, {} as Record<number, MessageReaction[]>);

    return Object.entries(grouped).map(([userId, reactions]) => ({
      userId: parseInt(userId),
      reactions: reactions
    }));
  };

  const groupedReactionsByUser = groupReactionsByUser(message.reactions);
  const currentUserIdNum = parseInt(currentUserId);

  // Adjust menu position when it's shown
  useEffect(() => {
    if (showActions) {
      // Small delay to ensure DOM is updated
      setTimeout(() => adjustMenuPosition(), 10);
    }
  }, [showActions]);

  return (
    <div 
      id={`message-${message.id}`}
      className={`flex ${isSender ? 'justify-end' : 'justify-start'} relative`}
    >
      <div className="relative" ref={messageRef}>
        <div
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          onTouchMove={handleTouchMove}
          onContextMenu={handleContextMenu}
          className={`max-w-xs px-4 py-3 rounded-2xl border text-white transition-colors ${
            isSender
              ? 'bg-[#12121A]/90 border-[#3B82F6]/70 rounded-br-sm shadow-[0_0_18px_rgba(59,130,246,0.35),inset_0_0_0_1px_rgba(124,58,237,0.22)]'
              : isOfflineMessage
              ? 'bg-[#12121A]/90 border-[#06B6D4]/55 text-white rounded-bl-sm shadow-[0_0_14px_rgba(6,182,212,0.18)]'
              : 'bg-[#12121A]/90 border-[#7C3AED]/30 text-white rounded-bl-sm shadow-[0_0_14px_rgba(124,58,237,0.14)]'
          }`}
        >
          {/* Reply Preview */}
          {message.replyToMessage && (
            <div 
              className={`mb-2 pb-2 border-l-[3px] ${
                isSender 
                  ? 'border-[#06B6D4]' 
                  : 'border-[#7C3AED]'
              } pl-2 text-xs opacity-75 cursor-pointer hover:opacity-100 transition-opacity bg-black/10 rounded-r`}
              onClick={(e) => {
                e.stopPropagation();
                // Scroll to the original message if needed
                const originalMessage = document.getElementById(`message-${message.replyToMessage?.id}`);
                if (originalMessage) {
                  originalMessage.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  originalMessage.classList.add('ring-2', 'ring-[#06B6D4]', 'ring-opacity-75', 'rounded-lg');
                  setTimeout(() => {
                    originalMessage.classList.remove('ring-2', 'ring-[#06B6D4]', 'ring-opacity-75', 'rounded-lg');
                  }, 2000);
                }
              }}
            >
              <div className="font-semibold text-xs mb-0.5 flex items-center gap-1">
                <span className="text-[#06B6D4]">↩</span>
                {replyToName}
              </div>
              <div className="truncate italic">
                {message.replyToMessage.type === 'Image' ? (
                  <span>📷 Image</span>
                ) : message.replyToMessage.type === 'Voice' ? (
                  <span>🎤 Voice message</span>
                ) : message.replyToMessage.type === 'YouTube' ? (
                  <span>🎬 YouTube link</span>
                ) : message.replyToMessage.type === 'AmazonMusic' ? (
                  <span>🎵 Amazon Music</span>
                ) : (
                  message.replyToMessage.content
                )}
              </div>
            </div>
          )}
          
          {message.type === 'Image' && imageUrl ? (
            <div>
              <img
                src={imageUrl}
                alt="Shared"
                className="max-w-full rounded-lg cursor-pointer mb-2"
                onClick={() => onImageClick(imageUrl)}
              />
              <p className="text-xs mt-1 opacity-70">{formatMessageTime(message.createdAtUtc)}</p>
            </div>
          ) : message.type === 'Voice' && voiceUrl ? (
            <div>
              <audio 
                src={voiceUrl} 
                controls 
                className="max-w-full"
                style={{ maxWidth: '250px' }}
              />
              <p className="text-xs mt-1 opacity-70">{formatMessageTime(message.createdAtUtc)}</p>
            </div>
          ) : youtubeUrl ? (
            <div className="space-y-2">
              <YouTubePreview url={youtubeUrl} />
              <p className="text-xs opacity-70">{formatMessageTime(message.createdAtUtc)}</p>
            </div>
          ) : amazonMusicUrl ? (
            <div className="space-y-2">
              <AmazonMusicPreview url={amazonMusicUrl} />
              <p className="text-xs opacity-70">{formatMessageTime(message.createdAtUtc)}</p>
            </div>
          ) : (
            <div>
              <p className="whitespace-pre-wrap break-words">{renderContentWithLinks(message.content)}</p>
              <p className="text-xs mt-1 opacity-70">{formatMessageTime(message.createdAtUtc)}</p>
            </div>
          )}
        </div>

        {groupedReactionsByUser.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {groupedReactionsByUser.map(({ userId, reactions }) => {
              const isCurrentUser = userId === currentUserIdNum;
              // Different colors and borders for each user
              // Current user: blue background with blue border
              // Other user: green/teal background with green border
              const bgColor = isCurrentUser 
                ? 'bg-[#3B82F6]/20 hover:bg-[#3B82F6]/30' 
                : 'bg-[#06B6D4]/18 hover:bg-[#06B6D4]/28';
              const borderColor = isCurrentUser 
                ? 'border-[#3B82F6]' 
                : 'border-[#06B6D4]';
              
              return (
                <div key={userId} className="flex gap-1 flex-wrap">
                  {reactions.map((reaction) => (
                    <button
                      key={reaction.id}
                      onClick={() => {
                        // Only allow removing own reactions
                        if (reaction.userId === currentUserIdNum) {
                          onRemoveReaction(reaction.id);
                        }
                      }}
                      className={`${bgColor} ${borderColor} border rounded-full px-2.5 py-1 text-base flex items-center justify-center transition-all hover:scale-110 active:scale-95 ${
                        reaction.userId === currentUserIdNum 
                          ? 'cursor-pointer ring-1 ring-[#3B82F6]/50' 
                          : 'cursor-default'
                      }`}
                      title={reaction.userId === currentUserIdNum ? 'Click to remove' : ''}
                    >
                      <span className="select-none">{reaction.emoji}</span>
                    </button>
                  ))}
                </div>
              );
            })}
          </div>
        )}

        {showActions && (
          <>
            <div 
              className="fixed inset-0 z-30" 
              onClick={() => setShowActions(false)}
            />
            <div 
              ref={actionsMenuRef}
              className={`absolute bottom-full mb-2 bg-[#09090B]/95 rounded-lg shadow-xl border border-[#7C3AED]/25 p-2 z-40 flex gap-2 whitespace-nowrap ${
                isSender ? 'right-0' : 'left-0'
              }`}
              style={{
                maxWidth: 'calc(100vw - 2rem)',
              }}
            >
              {onReplyToMessage && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onReplyToMessage(message);
                    setShowActions(false);
                  }}
                  className="flex items-center gap-2 px-3 py-2 hover:bg-[#7C3AED]/20 rounded transition-colors"
                >
                  <MdReply size={18} />
                  <span className="text-sm">Reply</span>
                </button>
              )}
              {/* Allow reactions on both own and opponent messages */}
              <button
                onClick={handleReactClick}
                className="flex items-center gap-2 px-3 py-2 hover:bg-[#7C3AED]/20 rounded transition-colors"
              >
                <MdAddReaction size={18} />
                <span className="text-sm">React</span>
              </button>
              {isSender && (
                <button
                  onClick={handleDelete}
                  className="flex items-center gap-2 px-3 py-2 hover:bg-red-700 rounded transition-colors text-red-400"
                >
                  <MdDelete size={18} />
                  <span className="text-sm">Delete</span>
                </button>
              )}
            </div>
          </>
        )}

        {showEmojiPicker && (
          <EmojiPicker
            onEmojiSelect={handleEmojiSelect}
            onClose={() => setShowEmojiPicker(false)}
            position={emojiPickerPos}
          />
        )}
      </div>
    </div>
  );
};

export default MessageItem;
